import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
   Box,
   Typography,
   Avatar,
   IconButton,
   useTheme,
   useMediaQuery,
   CircularProgress,
   Dialog,
   DialogContent,
   DialogTitle,
   DialogActions,
   Snackbar,
   Alert,
   Button,
} from '@mui/material';
import {
   ArrowForwardIos as ArrowForwardIcon,
   ArrowBackIos as ArrowBackIcon,
   Folder as FolderIcon,
   Close as CloseIcon,
   Download as DownloadIcon,
   Delete as DeleteIcon,
   OpenInNew as SeeAllIcon,
} from '@mui/icons-material';
import { getAuthHeaders } from '../../utils/auth';
import PhotoLikeButton from '../common/PhotoLikeButton';

interface Photo {
   id: string;
   url: string;
   title?: string;
   takenAt?: any;
   location?: any;
   uploadedBy?: string;
   uploadedAt?: any;
   mediaType?: string;
   videoUrl?: string;
   thumbnailUrl?: string;
   fileName?: string;
   likeCount?: number;
   isLiked?: boolean;
   uploaderProfile?: {
      displayName?: string;
      name?: string;
      fullName?: string;
      photoURL?: string;
      profilePicture?: string;
      profilePhoto?: string;
      avatar?: string;
   };
}

interface PhotoFolder {
   id: string;
   name: string;
   photoIds: string[];
   photoCount: number;
   createdAt?: any;
}

interface ClusterCardProps {
   folder: PhotoFolder;
   folderPhotos: Photo[];
   onSeeAll: () => void;
}

function ClusterCard({ folder, folderPhotos, onSeeAll }: ClusterCardProps) {
   const hero = folderPhotos[0];
   const thumbnails = folderPhotos.slice(1, 5);

   return (
      <Box
         onClick={onSeeAll}
         sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden',
            cursor: 'pointer',
            bgcolor: 'background.paper',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            transition: 'box-shadow 0.2s ease',
            '&:hover': {
               boxShadow: 4,
            },
            display: 'flex',
            flexDirection: 'column',
            minHeight: 200,
         }}
      >
         <Box
            sx={{
               flex: 1,
               minHeight: 180,
               display: 'grid',
               gridTemplateColumns: '1.2fr 1fr',
               gridTemplateRows: '1fr 1fr',
               gap: 0.5,
               p: 0.5,
            }}
         >
            <Box
               component="img"
               src={hero?.mediaType === 'video' ? (hero?.thumbnailUrl ?? '') : (hero?.url ?? '')}
               alt={hero?.title || folder.name}
               loading="lazy"
               sx={{
                  gridRow: '1 / -1',
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: 1,
               }}
            />
            {thumbnails.slice(0, 4).map((p, i) => (
               <Box
                  key={p.id}
                  component="img"
                  src={p.mediaType === 'video' ? (p.thumbnailUrl ?? '') : p.url}
                  alt={p.title || ''}
                  loading="lazy"
                  sx={{
                     width: '100%',
                     height: '100%',
                     objectFit: 'cover',
                     borderRadius: 1,
                  }}
               />
            ))}
         </Box>
         {hero?.uploaderProfile &&
            (hero.uploaderProfile.displayName || hero.uploaderProfile.name || hero.uploaderProfile.fullName) && (
            <Box
               sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.5,
                  py: 0.75,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'action.hover',
               }}
            >
               <Avatar
                  sx={{ width: 20, height: 20 }}
                  src={
                     hero.uploaderProfile.profilePicture ||
                     hero.uploaderProfile.photoURL ||
                     hero.uploaderProfile.profilePhoto ||
                     hero.uploaderProfile.avatar
                  }
               >
                  {(hero.uploaderProfile.displayName ||
                     hero.uploaderProfile.name ||
                     hero.uploaderProfile.fullName)
                     ?.charAt(0) || '?'}
               </Avatar>
               <Typography variant="caption" color="text.secondary" noWrap>
                  {hero.uploaderProfile.displayName ||
                     hero.uploaderProfile.name ||
                     hero.uploaderProfile.fullName}
               </Typography>
            </Box>
         )}
         <Box
            sx={{
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'space-between',
               px: 1.5,
               py: 1.25,
               borderTop: '1px solid',
               borderColor: 'divider',
               bgcolor: 'background.paper',
            }}
         >
            <Box sx={{ minWidth: 0 }}>
               <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }} noWrap>
                  {folder.name}
               </Typography>
               <Typography variant="caption" color="text.secondary">
                  {folder.photoCount} {folder.photoCount === 1 ? 'photo' : 'photos'}
               </Typography>
            </Box>
            <Typography
               variant="body2"
               sx={{
                  color: '#00C4A7',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.25,
                  flexShrink: 0,
               }}
            >
               See all
               <SeeAllIcon sx={{ fontSize: 16 }} />
            </Typography>
         </Box>
      </Box>
   );
}

interface PhotoGroupsBentoProps {
   photos: Photo[];
   albumId: string;
   userRole?: 'owner' | 'editor' | 'viewer' | null;
   onPhotoDeleted?: () => void;
   album?: any;
   currentUser?: any;
}

const PhotoGroupsBento: React.FC<PhotoGroupsBentoProps> = ({
   photos,
   albumId,
   userRole,
   onPhotoDeleted,
   album,
   currentUser,
}) => {
   const navigate = useNavigate();
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('md'));

   const [folders, setFolders] = useState<PhotoFolder[]>([]);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState<string | null>(null);

   const [modalOpen, setModalOpen] = useState(false);
   const [selectedFolder, setSelectedFolder] = useState<PhotoFolder | null>(null);
   const [modalPhotoIndex, setModalPhotoIndex] = useState(0);

   const [downloadingPhotoId, setDownloadingPhotoId] = useState<string | null>(null);
   const [snackbarOpen, setSnackbarOpen] = useState(false);
   const [snackbarMessage, setSnackbarMessage] = useState('');
   const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info'>('success');

   const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
   const [photoToDelete, setPhotoToDelete] = useState<Photo | null>(null);
   const [deletingPhoto, setDeletingPhoto] = useState(false);

   const [touchStart, setTouchStart] = useState<number | null>(null);
   const [touchEnd, setTouchEnd] = useState<number | null>(null);

   const thumbnailStripRef = useRef<HTMLDivElement | null>(null);

   const fetchPhotoFolders = async () => {
      try {
         setLoading(true);
         setError(null);
         const base = process.env.REACT_APP_API_URL || '';
         const headers = await getAuthHeaders();
         const response = await fetch(`${base}/api/albums/${albumId}/photo-groups`, { headers });

         if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const errorMessage =
               errorData.error || errorData.message || `Failed to fetch photo folders (${response.status})`;
            throw new Error(errorMessage);
         }
         const data = await response.json();
         setFolders(data.folders || []);
      } catch (err: any) {
         setError(err.message || 'Failed to load photo folders');
      } finally {
         setLoading(false);
      }
   };

   useEffect(() => {
      if (albumId && photos.length > 0) {
         fetchPhotoFolders();
      } else {
         setLoading(false);
      }
   }, [albumId, photos.length]);

   const getPhotosByIds = (photoIds: string[]): Photo[] => {
      return photoIds
         .map((id) => photos.find((p) => p.id === id))
         .filter((p): p is Photo => p !== undefined);
   };

   const handleSeeAll = (folder: PhotoFolder, startIndex: number = 0) => {
      setSelectedFolder(folder);
      setModalPhotoIndex(startIndex);
      setModalOpen(true);
   };

   const handleModalClose = () => {
      setModalOpen(false);
      setSelectedFolder(null);
      setModalPhotoIndex(0);
   };

   const handleModalNext = () => {
      if (selectedFolder) {
         const folderPhotos = getPhotosByIds(selectedFolder.photoIds);
         setModalPhotoIndex((prev) => (prev + 1) % folderPhotos.length);
      }
   };

   const handleModalPrev = () => {
      if (selectedFolder) {
         const folderPhotos = getPhotosByIds(selectedFolder.photoIds);
         setModalPhotoIndex((prev) => (prev - 1 + folderPhotos.length) % folderPhotos.length);
      }
   };

   useEffect(() => {
      const handleKeyPress = (e: KeyboardEvent) => {
         if (!modalOpen) return;
         if (e.key === 'ArrowLeft') handleModalPrev();
         else if (e.key === 'ArrowRight') handleModalNext();
         else if (e.key === 'Escape') handleModalClose();
      };
      window.addEventListener('keydown', handleKeyPress);
      return () => window.removeEventListener('keydown', handleKeyPress);
   }, [modalOpen, selectedFolder]);

   useEffect(() => {
      if (!modalOpen || !selectedFolder || !thumbnailStripRef.current) return;
      const el = thumbnailStripRef.current.children[modalPhotoIndex] as HTMLElement | undefined;
      el?.scrollIntoView({ inline: 'center', behavior: 'smooth', block: 'nearest' });
   }, [modalOpen, selectedFolder, modalPhotoIndex]);

   const minSwipeDistance = 50;
   const onTouchStart = (e: React.TouchEvent) => {
      setTouchEnd(null);
      setTouchStart(e.targetTouches[0].clientX);
   };
   const onTouchMove = (e: React.TouchEvent) => {
      setTouchEnd(e.targetTouches[0].clientX);
   };
   const onTouchEnd = () => {
      if (!touchStart || !touchEnd) return;
      const distance = touchStart - touchEnd;
      if (distance > minSwipeDistance) handleModalNext();
      else if (distance < -minSwipeDistance) handleModalPrev();
   };

   const handlePerPhotoPayment = async (photoId: string) => {
      if (!currentUser || !albumId || !album) return;
      setDownloadingPhotoId(photoId);
      try {
         const headers = await getAuthHeaders();
         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/albums/${albumId}/create-payment-session`,
            {
               method: 'POST',
               headers: { ...headers, 'Content-Type': 'application/json' },
               body: JSON.stringify({
                  userId: currentUser.uid,
                  email: currentUser.email,
                  albumTitle: album.title,
                  albumId,
                  photoId,
                  amount: album.pricePerPhoto || 5.0,
                  pricingModel: 'per_photo',
                  returnUrl: `${window.location.origin}/albums/${albumId}`,
                  cancelUrl: `${window.location.origin}/albums/${albumId}`,
               }),
            }
         );
         const result = await response.json();
         if (response.ok && result.sessionUrl) {
            localStorage.setItem(
               'pendingPhotoDownload',
               JSON.stringify({ albumId, photoId, paymentType: 'per_photo', timestamp: Date.now() })
            );
            window.location.href = result.sessionUrl;
         } else {
            setSnackbarMessage(result.message || 'Failed to create payment session');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            setDownloadingPhotoId(null);
         }
      } catch {
         setSnackbarMessage('An error occurred. Please try again.');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
         setDownloadingPhotoId(null);
      }
   };

   const handleDownloadPhoto = async (photoId: string) => {
      try {
         if (
            album?.type === 'paid' &&
            album?.pricingModel === 'per_photo' &&
            userRole !== 'owner'
         ) {
            try {
               const headers = await getAuthHeaders();
               const paymentCheckResponse = await fetch(
                  `${process.env.REACT_APP_API_URL}/api/albums/${albumId}/check-photo-payment/${photoId}`,
                  { headers }
               );
               if (paymentCheckResponse.ok) {
                  const paymentData = await paymentCheckResponse.json();
                  if (!paymentData.hasPaid) {
                     await handlePerPhotoPayment(photoId);
                     return;
                  }
               } else {
                  await handlePerPhotoPayment(photoId);
                  return;
               }
            } catch {
               await handlePerPhotoPayment(photoId);
               return;
            }
         }

         setDownloadingPhotoId(photoId);
         const headers = await getAuthHeaders();

         const trackDownload = async () => {
            try {
               const trackHeaders = await getAuthHeaders();
               const trackResponse = await fetch(
                  `${process.env.REACT_APP_API_URL}/api/albums/${albumId}/track-download`,
                  {
                     method: 'POST',
                     headers: { ...trackHeaders, 'Content-Type': 'application/json' },
                     body: JSON.stringify({ photoId }),
                  }
               );
               if (trackResponse.ok) {
                  const trackData = await trackResponse.json();
                  if (trackData.alreadyDownloaded) return { alreadyDownloaded: true };
               }
               return { alreadyDownloaded: false };
            } catch {
               return { alreadyDownloaded: false };
            }
         };

         const trackResult = await trackDownload();
         if (trackResult?.alreadyDownloaded) {
            setSnackbarMessage('You have already downloaded this photo');
            setSnackbarSeverity('info');
            setSnackbarOpen(true);
            setDownloadingPhotoId(null);
            return;
         }

         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/albums/${albumId}/photos/${photoId}/download`,
            { headers }
         );
         if (!response.ok) {
            let errorMessage = 'Failed to download photo';
            try {
               const errorData = await response.json();
               errorMessage = errorData.message || errorMessage;
            } catch {
               errorMessage = response.statusText || errorMessage;
            }
            throw new Error(errorMessage);
         }

         const contentDisposition = response.headers.get('content-disposition');
         let fileName = `photo-${photoId}.jpg`;
         if (contentDisposition) {
            const match = contentDisposition.match(/filename="(.+)"/);
            if (match?.[1]) fileName = match[1];
         }
         const blob = await response.blob();
         const blobUrl = URL.createObjectURL(blob);
         const link = document.createElement('a');
         link.href = blobUrl;
         link.download = fileName;
         link.style.display = 'none';
         document.body.appendChild(link);
         link.click();
         document.body.removeChild(link);
         URL.revokeObjectURL(blobUrl);

         setSnackbarMessage('Photo downloaded successfully!');
         setSnackbarSeverity('success');
         setSnackbarOpen(true);
      } catch (err: any) {
         setSnackbarMessage(err.message || 'Failed to download photo');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setDownloadingPhotoId(null);
      }
   };

   const handleDeletePhoto = async () => {
      if (!photoToDelete) return;
      try {
         setDeletingPhoto(true);
         const headers = await getAuthHeaders();
         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/albums/${albumId}/remove-photo`,
            {
               method: 'POST',
               headers,
               body: JSON.stringify({ photoId: photoToDelete.id }),
            }
         );
         if (!response.ok) {
            const responseText = await response.text();
            let errorMessage = 'Failed to remove photo from album';
            try {
               const errorData = JSON.parse(responseText);
               errorMessage = errorData.message || errorMessage;
            } catch {
               errorMessage = `Server error (${response.status}): ${response.statusText}`;
            }
            throw new Error(errorMessage);
         }
         setSnackbarMessage('Photo removed from album successfully');
         setSnackbarSeverity('success');
         setSnackbarOpen(true);
         setDeleteDialogOpen(false);
         setPhotoToDelete(null);
         setModalOpen(false);
         setSelectedFolder(null);
         setModalPhotoIndex(0);
         await fetchPhotoFolders();
         onPhotoDeleted?.();
      } catch (err: any) {
         setSnackbarMessage(err.message || 'Failed to remove photo');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setDeletingPhoto(false);
      }
   };

   if (loading) {
      return (
         <Box
            sx={{
               display: 'flex',
               flexDirection: 'column',
               alignItems: 'center',
               justifyContent: 'center',
               py: 8,
               px: 2,
            }}
         >
            <CircularProgress size={60} />
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
               Loading photo folders...
            </Typography>
         </Box>
      );
   }

   if (error) {
      return (
         <Box sx={{ p: 2 }}>
            <Alert severity="error">{error}</Alert>
         </Box>
      );
   }

   const foldersWithPhotos = folders.filter((f) => getPhotosByIds(f.photoIds).length > 0);
   if (foldersWithPhotos.length === 0) {
      return (
         <Box
            sx={{
               display: 'flex',
               flexDirection: 'column',
               alignItems: 'center',
               justifyContent: 'center',
               py: 8,
               px: 2,
            }}
         >
            <FolderIcon sx={{ fontSize: 80, color: 'grey.300', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
               No Photo Folders Yet
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
               Create folders in the Photos tab to organize your photos
            </Typography>
         </Box>
      );
   }

   return (
      <Box sx={{ pb: 4 }}>
         <Box sx={{ mb: 4, px: isMobile ? 2 : 0 }}>
            <Typography
               variant={isMobile ? 'h5' : 'h4'}
               sx={{ fontWeight: 700, color: 'text.primary', mb: 1, textAlign: 'center' }}
            >
               {foldersWithPhotos.length}{' '}
               {foldersWithPhotos.length === 1 ? 'Photo Folder' : 'Photo Folders'}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center' }}>
               Browse your organized photo collections
            </Typography>
         </Box>

         <Box
            sx={{
               display: 'grid',
               gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
               gap: 2,
               px: isMobile ? 2 : 0,
               gridAutoRows: 'minmax(200px, auto)',
            }}
         >
            {foldersWithPhotos.map((folder) => {
               const folderPhotos = getPhotosByIds(folder.photoIds);
               if (folderPhotos.length === 0) return null;
               return (
                  <ClusterCard
                     key={folder.id}
                     folder={folder}
                     folderPhotos={folderPhotos}
                     onSeeAll={() => handleSeeAll(folder)}
                  />
               );
            })}
         </Box>

         <Dialog
            open={modalOpen}
            onClose={handleModalClose}
            maxWidth="xl"
            fullWidth
            PaperProps={{
               sx: {
                  borderRadius: 3,
                  maxHeight: '95vh',
                  height: '95vh',
                  display: 'flex',
                  flexDirection: 'column',
               },
            }}
         >
            <DialogTitle
               sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  pb: 2,
               }}
            >
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                     sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: 'rgba(0, 196, 167, 0.15)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#00C4A7',
                     }}
                  >
                     <FolderIcon />
                  </Box>
                  <Box>
                     <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {selectedFolder?.name}
                     </Typography>
                     <Typography variant="body2" color="text.secondary">
                        {selectedFolder?.photoCount}{' '}
                        {selectedFolder?.photoCount === 1 ? 'photo' : 'photos'}
                     </Typography>
                  </Box>
               </Box>
               <IconButton onClick={handleModalClose} size="small">
                  <CloseIcon />
               </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 2, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
               {selectedFolder && (() => {
                  const folderPhotos = getPhotosByIds(selectedFolder.photoIds);
                  const selectedPhoto = folderPhotos[modalPhotoIndex];
                  const thumbSize = 96;
                  const maxDots = 20;
                  const dotCount = Math.min(folderPhotos.length, maxDots);
                  const dotStep = folderPhotos.length <= maxDots ? 1 : folderPhotos.length / maxDots;
                  const activeDotIndex = folderPhotos.length <= maxDots
                     ? modalPhotoIndex
                     : Math.min(Math.floor(modalPhotoIndex / dotStep), dotCount - 1);
                  return (
                     <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                        {/* Pagination dots */}
                        {folderPhotos.length > 1 && (
                           <Box
                              sx={{
                                 display: 'flex',
                                 justifyContent: 'center',
                                 gap: 0.5,
                                 mb: 1.5,
                                 flexWrap: 'wrap',
                              }}
                              role="list"
                              aria-label="Photo position"
                           >
                              {Array.from({ length: dotCount }, (_, i) => (
                                 <Box
                                    key={i}
                                    role="listitem"
                                    sx={{
                                       width: 8,
                                       height: 8,
                                       borderRadius: '50%',
                                       bgcolor: i === activeDotIndex ? '#00C4A7' : 'grey.400',
                                       flexShrink: 0,
                                    }}
                                 />
                              ))}
                           </Box>
                        )}

                        {/* Thumbnail strip */}
                        <Box
                           ref={thumbnailStripRef}
                           sx={{
                              display: 'flex',
                              gap: 1,
                              overflowX: 'auto',
                              overflowY: 'hidden',
                              pb: 1,
                              scrollSnapType: 'x mandatory',
                              '& > *': { scrollSnapAlign: 'center' },
                              '&::-webkit-scrollbar': { height: 6 },
                           }}
                        >
                           {folderPhotos.map((photo, i) => (
                              <Box
                                 key={photo.id}
                                 onClick={() => setModalPhotoIndex(i)}
                                 sx={{
                                    width: thumbSize,
                                    height: thumbSize,
                                    minWidth: thumbSize,
                                    minHeight: thumbSize,
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    position: 'relative',
                                    cursor: 'pointer',
                                    border: '2px solid',
                                    borderColor: modalPhotoIndex === i ? '#00C4A7' : 'transparent',
                                    bgcolor: 'grey.200',
                                 }}
                                 aria-current={modalPhotoIndex === i ? 'true' : undefined}
                              >
                                 <Box
                                    component="img"
                                    src={photo.mediaType === 'video' ? (photo.thumbnailUrl ?? photo.url) : photo.url}
                                    alt={photo.title || `Photo ${i + 1}`}
                                    loading="lazy"
                                    sx={{
                                       width: '100%',
                                       height: '100%',
                                       objectFit: 'cover',
                                    }}
                                 />
                                 <Typography
                                    variant="caption"
                                    sx={{
                                       position: 'absolute',
                                       bottom: 2,
                                       left: 2,
                                       color: 'white',
                                       fontWeight: 700,
                                       textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                                    }}
                                 >
                                    {String(i + 1).padStart(2, '0')}
                                 </Typography>
                              </Box>
                           ))}
                           <Box
                              sx={{
                                 display: 'flex',
                                 alignItems: 'center',
                                 flexShrink: 0,
                                 px: 1,
                                 color: 'grey.500',
                              }}
                              aria-hidden
                           >
                              <ArrowForwardIcon sx={{ fontSize: 28 }} />
                           </Box>
                        </Box>

                        {/* Selected photo preview - fills available space so video fits display */}
                        <Box
                           onTouchStart={onTouchStart}
                           onTouchMove={onTouchMove}
                           onTouchEnd={onTouchEnd}
                           sx={{
                              width: '100%',
                              flex: 1,
                              minHeight: isMobile ? 280 : 420,
                              position: 'relative',
                              borderRadius: 2,
                              overflow: 'hidden',
                              bgcolor: 'grey.100',
                              mt: 2,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                           }}
                        >
                           {selectedPhoto && (
                              <>
                                 <Box
                                    sx={{
                                       position: 'absolute',
                                       inset: 0,
                                       display: 'flex',
                                       alignItems: 'center',
                                       justifyContent: 'center',
                                    }}
                                 >
                                    {selectedPhoto.mediaType === 'video' ? (
                                       <video
                                          key={modalPhotoIndex}
                                          src={selectedPhoto.url || selectedPhoto.videoUrl}
                                          poster={selectedPhoto.thumbnailUrl}
                                          controls
                                          playsInline
                                          style={{
                                             maxWidth: '100%',
                                             maxHeight: '100%',
                                             width: '100%',
                                             height: '100%',
                                             objectFit: 'contain',
                                          }}
                                       />
                                    ) : (
                                       <img
                                          src={selectedPhoto.url}
                                          alt={selectedPhoto.title || `Photo ${modalPhotoIndex + 1}`}
                                          style={{
                                             maxWidth: '100%',
                                             maxHeight: '100%',
                                             width: '100%',
                                             height: '100%',
                                             objectFit: 'contain',
                                          }}
                                       />
                                    )}
                                 </Box>
                                 <Box
                                    sx={{
                                       position: 'absolute',
                                       top: 12,
                                       right: 12,
                                       display: 'flex',
                                       gap: 1,
                                       zIndex: 2,
                                    }}
                                 >
                                    {(userRole === 'owner' || userRole === 'editor') && (
                                       <IconButton
                                          onClick={() => {
                                             setPhotoToDelete(selectedPhoto);
                                             setDeleteDialogOpen(true);
                                          }}
                                          sx={{
                                             bgcolor: 'rgba(211, 47, 47, 0.9)',
                                             color: 'white',
                                             boxShadow: 3,
                                             '&:hover': { bgcolor: '#d32f2f' },
                                          }}
                                          title="Remove photo from album"
                                       >
                                          <DeleteIcon />
                                       </IconButton>
                                    )}
                                    {currentUser && (
                                       <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'flex', alignItems: 'center' }}>
                                          <PhotoLikeButton
                                             photoId={selectedPhoto.id}
                                             userId={currentUser.uid}
                                             initialLikeCount={selectedPhoto.likeCount ?? 0}
                                             initialIsLiked={selectedPhoto.isLiked ?? false}
                                             albumId={albumId}
                                             size="small"
                                             variant="compact"
                                             showCount
                                          />
                                       </Box>
                                    )}
                                    <IconButton
                                       onClick={() => handleDownloadPhoto(selectedPhoto.id)}
                                       disabled={downloadingPhotoId === selectedPhoto.id}
                                       sx={{
                                          bgcolor: 'rgba(0, 196, 167, 0.9)',
                                          color: 'white',
                                          boxShadow: 3,
                                          '&:hover': { bgcolor: '#00C4A7' },
                                          '&:disabled': { bgcolor: 'rgba(0, 196, 167, 0.5)' },
                                       }}
                                       title="Download photo"
                                    >
                                       {downloadingPhotoId === selectedPhoto.id ? (
                                          <CircularProgress size={20} sx={{ color: 'white' }} />
                                       ) : (
                                          <DownloadIcon />
                                       )}
                                    </IconButton>
                                 </Box>
                                 <IconButton
                                    onClick={handleModalPrev}
                                    sx={{
                                       position: 'absolute',
                                       left: 12,
                                       top: '50%',
                                       transform: 'translateY(-50%)',
                                       bgcolor: 'rgba(255, 255, 255, 0.9)',
                                       color: '#00C4A7',
                                       boxShadow: 3,
                                       '&:hover': { bgcolor: '#00C4A7', color: 'white' },
                                    }}
                                 >
                                    <ArrowBackIcon />
                                 </IconButton>
                                 <IconButton
                                    onClick={handleModalNext}
                                    sx={{
                                       position: 'absolute',
                                       right: 12,
                                       top: '50%',
                                       transform: 'translateY(-50%)',
                                       bgcolor: 'rgba(255, 255, 255, 0.9)',
                                       color: '#00C4A7',
                                       boxShadow: 3,
                                       '&:hover': { bgcolor: '#00C4A7', color: 'white' },
                                    }}
                                 >
                                    <ArrowForwardIcon />
                                 </IconButton>
                              </>
                           )}
                        </Box>

                        {selectedPhoto?.uploaderProfile &&
                           (selectedPhoto.uploaderProfile.displayName ||
                              selectedPhoto.uploaderProfile.name ||
                              selectedPhoto.uploaderProfile.fullName) && (
                           <Box
                              sx={{
                                 display: 'flex',
                                 alignItems: 'center',
                                 gap: 1,
                                 mt: 1.5,
                                 px: 0.5,
                              }}
                           >
                              <Avatar
                                 sx={{ width: 24, height: 24 }}
                                 src={
                                    selectedPhoto.uploaderProfile.profilePicture ||
                                    selectedPhoto.uploaderProfile.photoURL ||
                                    selectedPhoto.uploaderProfile.profilePhoto ||
                                    selectedPhoto.uploaderProfile.avatar
                                 }
                              >
                                 {(selectedPhoto.uploaderProfile.displayName ||
                                    selectedPhoto.uploaderProfile.name ||
                                    selectedPhoto.uploaderProfile.fullName)
                                    ?.charAt(0) || '?'}
                              </Avatar>
                              <Typography variant="body2" color="text.secondary">
                                 {selectedPhoto.uploaderProfile.displayName ||
                                    selectedPhoto.uploaderProfile.name ||
                                    selectedPhoto.uploaderProfile.fullName}
                              </Typography>
                           </Box>
                        )}

                        {/* Footer */}
                        <Box
                           sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-end',
                              gap: 0.5,
                              mt: 2,
                              color: 'text.secondary',
                           }}
                        >
                           <Typography variant="body2">
                              {selectedFolder.photoCount}{' '}
                              {selectedFolder.photoCount === 1 ? 'photo' : 'photos'}
                           </Typography>
                           <ArrowForwardIcon sx={{ fontSize: 18 }} />
                        </Box>
                     </Box>
                  );
               })()}
            </DialogContent>
         </Dialog>

         <Dialog
            open={deleteDialogOpen}
            onClose={() => !deletingPhoto && setDeleteDialogOpen(false)}
         >
            <DialogTitle>Remove Photo from Album</DialogTitle>
            <DialogContent>
               <Typography variant="body1">
                  Are you sure you want to remove this photo from the album?
               </Typography>
               {photoToDelete && (
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                     <img
                        src={photoToDelete.url}
                        alt={photoToDelete.title || 'Photo'}
                        style={{
                           maxWidth: '100%',
                           maxHeight: 200,
                           objectFit: 'contain',
                           borderRadius: 4,
                        }}
                     />
                  </Box>
               )}
               <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Note: This will only remove the photo from this album. The photo will remain in
                  your library.
               </Typography>
            </DialogContent>
            <DialogActions>
               <Button
                  onClick={() => setDeleteDialogOpen(false)}
                  disabled={deletingPhoto}
               >
                  Cancel
               </Button>
               <Button
                  onClick={handleDeletePhoto}
                  color="error"
                  variant="contained"
                  disabled={deletingPhoto}
                  startIcon={
                     deletingPhoto ? <CircularProgress size={20} color="inherit" /> : null
                  }
               >
                  {deletingPhoto ? 'Removing...' : 'Remove'}
               </Button>
            </DialogActions>
         </Dialog>

         <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={() => setSnackbarOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
         >
            <Alert
               onClose={() => setSnackbarOpen(false)}
               severity={snackbarSeverity}
               sx={{
                  width: '100%',
                  backgroundColor: snackbarSeverity === 'info' ? '#00C4A7' : undefined,
                  color: snackbarSeverity === 'info' ? 'white' : undefined,
                  '& .MuiAlert-message': {
                     color: snackbarSeverity === 'info' ? 'white' : undefined,
                  },
                  '& .MuiAlert-icon': {
                     color: snackbarSeverity === 'info' ? 'white' : undefined,
                  },
               }}
               action={
                  snackbarSeverity === 'info' ? (
                     <Button
                        size="small"
                        onClick={() => {
                           setSnackbarOpen(false);
                           navigate('/personal-photos?tab=downloaded');
                        }}
                        sx={{
                           color: 'white',
                           border: '1px solid white',
                           '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                        }}
                     >
                        View
                     </Button>
                  ) : null
               }
            >
               {snackbarMessage}
            </Alert>
         </Snackbar>
      </Box>
   );
};

export default PhotoGroupsBento;
